import { WORDS_BEFORE_AND_AFTER_SEARCH_TERM } from 'constants';
import AbstractModelType from '../AbstractModelType';
import { cfiLogic, documentUtilities, getBlockParentElement } from 'utilities';
import { stripSubresources } from 'utilities/documentUtilities';

const environment = process.env.REACT_APP_NODE_ENV || process.env.NODE_ENV;

/**
 * Computes information about the words in the book's content.
 */
class BookDocumentInformation extends AbstractModelType {
  static MODES = {
    NO_DOM: 0, // do not track DOM elements and let the target document be garbage collected
    NO_RANGE_RECTANGLES: 1, // include DOM, but do not compute rectangles or word continuations
    COMPUTE_SPATIAL_INFO: 2, // computes rectangles and uses them to guess word continuations
  };
  static temporaryFrameClassName = 'book-document-information-temporary-frame';

  #bookDocument;
  #frameDocument;

  // An ordered array of objects with information about each word in the content.
  #wordObjects = [];

  // A cache of mappings to find what word exists at a given character offset in the content.
  #wordOffsets = {};

  // map of marker element to its marker and markerIndex to its wordOffsets.
  #markerElementToMarker = {};
  #markerIndexToWordOffset = {};

  // Whether or not to compute bounding rectangles and word continuations for each word.
  #mode = this.constructor.MODES.NO_DOM;

  // A ratio to convert character offsets from Lunr search results to character offsets in the BookDocumentInformation.
  // They may be different due to differences in how the browser and Lunr count characters, especially whitespace.
  // The difference tends to be subtle.
  #lunrCharacterRatio = 1;

  constructor(frameDocument, bookDocument) {
    super(...arguments);

    this.#frameDocument = frameDocument;
    this.#bookDocument = bookDocument;

    // only considering topLevelMarkers for now, as UI only shows top level markers.
    bookDocument.topLevelMarkers.forEach((marker) => {
      // If the marker has no element, it points to top of the document, so wordOffsetForMarker is 0.
      this.#markerIndexToWordOffset[marker.index] = !marker.element
        ? 0
        : undefined;

      if (marker.element) {
        this.#markerElementToMarker[marker.element] = marker;
      }
    });
  }

  static cleanup() {
    document
      .querySelectorAll(`.${this.temporaryFrameClassName}`)
      .forEach((frame) => {
        frame.remove();
      });
  }

  /**
   * Given a BookDocument, build an instance by loading the document into a hidden iframe.
   *
   * @param {BookDocument} bookDocument - an instance of BookDocument
   * @returns {Promise<Document>} a promise that resolves to a new instance of BookDocumentInformation
   */
  static async createFromBookDocument(bookDocument) {
    const response = await fetch(bookDocument.sourceURL);
    let html = await response.text();

    // If we don't care about where anything is positioned,
    // we can remove all external resources to avoid extra network requests.
    // This means that the maximum MODE for this instance will be NO_RANGE_RECTANGLES.
    // If the book happens to be encrypted, most of the body content will not be affected by this.
    // So the shouldStripSubresources option is set in the prepareDocumentHTML call to handle the body there.
    html = stripSubresources(html);

    const htmlBlob = new Blob([html], { type: 'application/xhtml+xml' });

    // Construct a hidden iframe to access the document.
    this.cleanup(); // remove any existing frames
    const frame = document.createElement('iframe');
    frame.className = this.temporaryFrameClassName;
    frame.style.width = '0px';
    frame.style.height = '0px';
    document.body.appendChild(frame);

    let loadPromise;

    // In test environments, we cannot use the blob URL, so we must write the HTML directly to the frame.
    if (environment !== 'test') {
      frame.src = URL.createObjectURL(htmlBlob);

      // The load event may not run in test env.
      loadPromise = new Promise((resolve) => {
        frame.addEventListener('load', resolve);
      });

      await loadPromise;
    } else {
      frame.contentDocument.write(html);
    }

    await documentUtilities.prepareDocumentHTML(frame, {
      shouldAddMathJax: false,
      shouldStripSubresources: true,
    });

    return new this(frame.contentDocument, bookDocument);
  }

  get mode() {
    return this.#mode;
  }

  get wordObjects() {
    return this.#wordObjects;
  }

  /**
   * Determine whether a "word" should be merged with a previous "word," primarily based on distances.
   * Example: A large script "W" starting a page, followed by a contigent "elcome"
   *
   * Because the styling wrappers forces the text into two separate nodes, this is necessary to determine that they in fact are parts of the same word.
   * This also re-combines words that are split into separate nodes by our highlighting spans.
   *
   * @param {Node} newWordNode - the node that the new word belongs to
   * @param {DOMRect} newWordRangeRectangle - the bounding rectangle for the new word's text
   * @param {Object} previousWordObject - the object containing all information about the previous word
   * @returns {Boolean} whether or not the words ought to be merged
   */
  continuesPreviousWord(
    newWordNode,
    newWordRangeRectangle,
    previousWordObject,
  ) {
    const distanceThreshold = 0.01; // there are cases where two elements are separated by ~0.0001 pixels
    const overlapThreshold = 10; // avoid words on another line

    // first word
    if (!previousWordObject) {
      return false;
    }

    // the previous text node had a trailing space or newline character
    if (previousWordObject.endsInSpace || previousWordObject.endsInNewline) {
      return false;
    }

    // if the previous word and current word are part of the same text node
    // (and were split on a space or newline) they must be two different words.
    if (previousWordObject.nodes.includes(newWordNode)) {
      return false;
    }

    const closeEnough =
      newWordRangeRectangle.left - previousWordObject.rangeRectangle.right <=
      distanceThreshold;
    const notTooClose =
      previousWordObject.rangeRectangle.right - newWordRangeRectangle.left <=
      overlapThreshold;
    const sharingTheSameLine =
      previousWordObject.rangeRectangle.bottom > newWordRangeRectangle.top;

    if (closeEnough && notTooClose && sharingTheSameLine) {
      return true;
    }

    return false;
  }

  /**
   * A helper for combining word objects.
   * Returns a new object that represents the rectangle formed by the union of two DOMRect objects.
   *
   * @param {Object} rangeRectangle1 - DOMRect or DOMRect-like object
   * @param {Object} rangeRectangle2 - DOMRect or DOMRect-like object
   * @return {Object} combined DOMRect-like object
   */
  mergeRangeRectangles(rangeRectangle1, rangeRectangle2) {
    const newRangeRectangle = {
      top: Math.min(rangeRectangle1.top, rangeRectangle2.top),
      right: Math.max(rangeRectangle1.right, rangeRectangle2.right),
      bottom: Math.max(rangeRectangle1.bottom, rangeRectangle2.bottom),
      left: Math.min(rangeRectangle1.left, rangeRectangle2.left),
    };

    newRangeRectangle.width = newRangeRectangle.right - newRangeRectangle.left;
    newRangeRectangle.height = newRangeRectangle.bottom - newRangeRectangle.top;

    return newRangeRectangle;
  }

  /**
   * Creates an object to represent a word in a text node, or combines expanded information with the previous word object.
   *
   * @param {String} wordText - text of the word to process
   * @param {Node} node - node that contains the text
   * @param {Integer} characterOffsetInNode - offset within the text node
   * @param {Integer} bufferLength - length of the word including any trailing spaces or newlines, in order to track the next word's char offsets
   * @param {Object} previousWordObject - object with information about the preceding word
   * @returns {Object|Null} the new word object, or null if no new word object
   */
  #processWord(
    wordText,
    node,
    characterOffsetInNode,
    bufferLength,
    previousWordObject,
  ) {
    if (!wordText.length) {
      return null;
    }

    const range = document.createRange();
    if (node.nodeType === Node.TEXT_NODE) {
      range.setStart(node, characterOffsetInNode);
      range.setEnd(node, characterOffsetInNode + wordText.length);
    } else {
      // not a real text node, but should have a real parent
      range.setStart(node.parentElement, 0);
      range.setEnd(node.parentElement, 0);
    }

    const trackDOM = this.#mode > this.constructor.MODES.NO_DOM;
    const parentElement = trackDOM ? getBlockParentElement(node) : undefined;

    let rangeRectangle;

    if (this.#mode >= this.constructor.MODES.COMPUTE_SPATIAL_INFO) {
      rangeRectangle = range.getBoundingClientRect();

      if (
        this.continuesPreviousWord(node, rangeRectangle, previousWordObject)
      ) {
        previousWordObject.wordText += wordText;
        previousWordObject.rangeRectangle = this.mergeRangeRectangles(
          previousWordObject.rangeRectangle,
          rangeRectangle,
        );
        previousWordObject.parentElements.push(parentElement);
        previousWordObject.nodes.push(node);

        return null;
      }
    }

    const characterOffset = previousWordObject
      ? previousWordObject.characterOffset + previousWordObject.bufferLength
      : 0;

    const newWordObject = {
      bufferLength,
      characterOffset,
      characterOffsetInNode,
      endsInNewline: false,
      endsInSpace: false,
      nodes: trackDOM ? [node] : [],
      primaryNode: trackDOM ? node : undefined,
      parentElements: trackDOM ? [parentElement] : [],
      rangeRectangle,
      wordText,
    };

    // make JSON representation cleaner for tests
    newWordObject.toJSON = function () {
      return JSON.stringify([
        newWordObject.nodes.length,
        newWordObject.parentElements.length,
        newWordObject.characterOffset,
        newWordObject.wordText,
        newWordObject.bufferLength,
        newWordObject.endsInSpace,
        newWordObject.endsInNewline,
      ]);
    };

    return newWordObject;
  }

  /**
   * Process a text node, extracting what it believes are words as it finds them.
   * Uses spaces, newlines, and the end of the node to liberally delimit words.
   *
   * The #processWord function that operates on each word may decide to merge "words" back together.
   *
   * @param {Node} node - the text node to process
   * @param {Object} previousWordObject - object with information about the most recently constructed word object
   * @returns {Array} an array of all generated word objects
   */
  #processTextNode(node, previousWordObject) {
    let text = node.textContent;
    let buffer = '';
    let newWordObjects = [];
    let characterOffsetInNode = 0;

    for (let char of text) {
      buffer += char;

      // encountered a space
      if (buffer.endsWith(' ')) {
        let wordWithoutSpace = buffer.substring(0, buffer.length - 1);
        let newWordObject = this.#processWord(
          wordWithoutSpace,
          node,
          characterOffsetInNode,
          buffer.length,
          previousWordObject,
        );
        if (newWordObject) {
          newWordObjects.push(newWordObject);
          previousWordObject = newWordObject;
        }
        characterOffsetInNode += buffer.length;
        if (previousWordObject) {
          previousWordObject.endsInSpace = true;
        }
        buffer = '';
        continue;
      }

      // encountered a newline
      if (buffer.endsWith('\n')) {
        let wordWithoutNewline = buffer.substring(0, buffer.length - 1);
        let newWordObject = this.#processWord(
          wordWithoutNewline,
          node,
          characterOffsetInNode,
          buffer.length,
          previousWordObject,
        );
        if (newWordObject) {
          newWordObjects.push(newWordObject);
          previousWordObject = newWordObject;
        }
        characterOffsetInNode += buffer.length;
        if (previousWordObject) {
          previousWordObject.endsInNewline = true;
        }
        buffer = '';
        continue;
      }
    }

    // remainder of text node becomes a word
    if (buffer.length) {
      let newWordObject = this.#processWord(
        buffer,
        node,
        characterOffsetInNode,
        buffer.length,
        previousWordObject,
      );
      if (newWordObject) {
        newWordObjects.push(newWordObject);
        previousWordObject = newWordObject;
      }
    }

    return newWordObjects;
  }

  isCompatibleElementNode(node) {
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return false;
    }

    const tagName = (node.tagName || '').toLowerCase();

    if (['img', 'math'].includes(tagName)) {
      return false;
    }

    // not rendered
    if (window.getComputedStyle(node).getPropertyValue('display') === 'none') {
      return false;
    }

    // is a page number
    const epubType = (node.getAttribute('epub:type') || '').toLowerCase();
    if (epubType === 'pagebreak') {
      return false;
    }

    return true;
  }

  /**
   * Recursively iterates all elements and text nodes in an element, building word objects.
   * Checks for node id to determine the wordOffsetForMarker.
   *
   * @param {Element} element - iterate the child nodes of this element
   * @param {Object} previousWordObject - word object that precedes the next one to be created
   * @returns {Array} an array of word objects
   */
  #buildWordObjects(element, previousWordObject = null) {
    let wordObjects = [];
    const nodes = Array.from(element.childNodes);

    for (let node of nodes) {
      const tagName = (node.tagName || '').toLowerCase();

      if (['script', 'style'].includes(tagName)) {
        continue;
      }

      if (node.id) {
        // Setting the wordOffsetForMarker for the current marker.
        const markerForNode = this.#markerElementToMarker[node.id];
        if (markerForNode) {
          this.#markerIndexToWordOffset[markerForNode.index] =
            wordObjects.length;
        }
      }

      if (cfiLogic.isBlacklisted(node)) {
        // don't include text of blacklisted nodes in word objects
        continue;
      }

      if (tagName === 'img' && node.alt) {
        const fakeTextNodeFromImage = {
          textContent: `Image with the following description: "${node.alt}"`,
          parentElement: node,
          toJSON: () => {
            return '{}'; // make it a little less verbose in tests
          },
        };

        wordObjects = wordObjects.concat(
          this.#processTextNode(fakeTextNodeFromImage, previousWordObject),
        );
      }

      if (this.isCompatibleElementNode(node)) {
        wordObjects = wordObjects.concat(
          this.#buildWordObjects(node, previousWordObject),
        );
      }

      if (node.nodeType === Node.TEXT_NODE) {
        wordObjects = wordObjects.concat(
          this.#processTextNode(node, previousWordObject),
        );
      }

      previousWordObject =
        wordObjects[wordObjects.length - 1] || previousWordObject;
    }

    return wordObjects;
  }

  // Given a character offset, find the word offset.
  wordOffsetFromCharacterOffset(characterOffset) {
    if (this.#wordOffsets[characterOffset]) {
      return this.#wordOffsets[characterOffset];
    }

    for (
      let wordOffset = this.wordObjects.length - 1;
      wordOffset >= 0;
      wordOffset -= 1
    ) {
      if (this.wordObjects[wordOffset].characterOffset <= characterOffset) {
        this.#wordOffsets[characterOffset] = wordOffset;
        return wordOffset;
      }
    }

    return 0;
  }

  buildWordObjects(mode = this.constructor.MODES.NO_RANGE_RECTANGLES) {
    this.#mode = mode;

    this.#wordObjects = this.#buildWordObjects(this.#frameDocument.body);

    if (mode > this.constructor.MODES.NO_DOM) {
      // When building word objects, also compute the number of characters as Lunr sees the document,
      // and then use this to define a ratio to translate lunr search result character offsets into BookDocumentInformation character offsets.
      // See platform repository for the same regex logic in lunr_search/plainText.js

      const lunrCharacterCount = this.#frameDocument.body.textContent
        .trim()
        .replace(/\s+/g, ' ').length;

      const lastWordObject = this.wordObjects[this.wordObjects.length - 1];

      this.#lunrCharacterRatio =
        lunrCharacterCount /
        (lastWordObject.characterOffset + lastWordObject.bufferLength);
    }
  }

  /**
   * Given a term, a character offset, and a length,
   * find a match with the nearest offset within wordObjects that starts with the term and has the given length.
   *
   * The term (returned by Lunr) is the base of a searched word, and the length is the length of a found word.
   * A search for "searching" may return a term of "search" and a length of 8 if the found word is "searched."
   *
   * Because of differences in how the browser and the server count characters and words,
   * the returned word offset may not be at the exact given character offset.
   * So this method exists to provide a correction, if necessary.
   *
   * @param {String} term
   * @param {Integer} lunrCharacterOffset
   * @param {Integer} lunrLength
   * @returns {Integer|null} a word offset, or null if no match
   */
  findWordOffset(
    term,
    lunrCharacterOffset,
    lunrLength,
    alreadyFoundWordOffsets = [],
  ) {
    // We may want to change this limit.
    // In most cases, the correct result should not be very far from the given lunr offset, and if it is it may not be the correct instance of the found term.
    // On a very very large manifest item, the time spent traversing the entire document back and forth for each result will add up.
    const MAX_OFFSET_ADJUSTMENT = 10000;

    // Convert the lunr character offset to a BookDocumentInformation character offset.
    const characterOffset = Math.round(
      lunrCharacterOffset * this.#lunrCharacterRatio,
    );

    const wordOffsetInitialPrediction =
      this.wordOffsetFromCharacterOffset(characterOffset);
    for (
      let offsetAdjustment = 0;
      offsetAdjustment <= MAX_OFFSET_ADJUSTMENT;
      // 0, 1, -1, 2, -2, 3, -3, ...
      offsetAdjustment =
        offsetAdjustment > 0 ? offsetAdjustment * -1 : offsetAdjustment * -1 + 1
    ) {
      const adjustedOffset = wordOffsetInitialPrediction + offsetAdjustment;
      if (this.wordObjects[adjustedOffset]) {
        // Lunr considers hyphens as word separators, so we need to as well.
        // A good example of this in Ghost Canyon is "ghost" "horse" and "ghost-horse"
        const wordTexts =
          this.wordObjects[adjustedOffset].wordText.match(/.*?-|.+$/g);

        for (let wordText of wordTexts) {
          // Mimic Lunr's default trimmer function, which removes non-word characters from the beginning and end of a word.
          // https://github.com/olivernn/lunr.js/blob/master/lib/trimmer.js
          const trimmedTerm = wordText
            .replace(/^\W+/, '')
            .replace(/\W+$/, '')
            .toLowerCase();

          // The word in the document may include characters that Lunr ignores.
          // And trimming it may reduce it to fewer characters than Lunr expects.
          // So consider the found term a match if the Lunr length expectation falls between the trimmed term length and the initial found term length.
          const doesLengthFallInRange =
            lunrLength >= trimmedTerm.length && lunrLength <= wordText.length;

          // Check that the term is the same length as the Lunr term and starts with the Lunr term.
          const isGoodMatch =
            doesLengthFallInRange &&
            !alreadyFoundWordOffsets.includes(adjustedOffset) &&
            trimmedTerm.startsWith(
              term.toLowerCase().replace(/(a|e|i|o|u|y)$/, ''),
            );
          // Not all Lunr word stems are subsets of found words.
          // For instance it will return "bunni" for "bunny" and "bunnies."
          // Cutting off trailing vowels for now as a naive solution.
          // If we see false positives, we can create a more complex algorithm that weighs
          // the similarity of strings along with the distance from the expected word offset.

          if (isGoodMatch) {
            return adjustedOffset;
          }
        }
      }
    }

    return null;
  }

  /**
   * Simple helper method to take an array of offsets and group them into nested arrays of adjacent offsets.
   *
   * This stems from the fact that Lunr separates multi-word searches into separate terms,
   * but those terms may be adjacent to each other in the content.
   * We can merge the words with adjacent offsets into single results for the user.
   *
   * @param  {...Integer} offsets
   * @returns {Array} nested array of arrays that groups offsets that are adjacent to each other
   */
  combineWordOffsets(...offsets) {
    let lastOffset;
    let nestedWordOffsets = [];

    offsets.sort((a, b) => a - b);
    offsets = [...new Set(offsets)];
    for (let offset of offsets) {
      if (lastOffset && lastOffset + 1 === offset) {
        nestedWordOffsets[nestedWordOffsets.length - 1].push(offset);
      } else {
        nestedWordOffsets.push([offset]);
      }

      lastOffset = offset;
    }

    return nestedWordOffsets;
  }

  /**
   * Generates a simple HTML string for a search result.
   *
   * It will return the found terms, wrap them in a span with a class of "found,"
   * and include a number of words of context before and after the found terms.
   *
   * @param {Array} wordOffsets array of integers that represent word offsets
   * @returns
   */
  buildSearchResult(wordOffsets) {
    const wordOffsetTermStart = wordOffsets[0];
    const wordOffsetTermEnd = wordOffsets[wordOffsets.length - 1];

    // Ensure that the result includes the context words, but does not go out of bounds.
    const wordOffsetResultStart = Math.max(
      0,
      wordOffsetTermStart - WORDS_BEFORE_AND_AFTER_SEARCH_TERM,
    );
    const wordOffsetResultEnd = Math.min(
      wordOffsetTermEnd + WORDS_BEFORE_AND_AFTER_SEARCH_TERM,
      this.wordObjects.length - 1,
    );

    const resultArray = [];
    let foundString = '';
    let reachedFoundStringBeginning = false;
    let reachedFoundStringEnd = false;

    for (
      let wordOffset = wordOffsetResultStart;
      wordOffset <= wordOffsetResultEnd;
      wordOffset += 1
    ) {
      if (wordOffset === wordOffsetTermStart) {
        resultArray.push('<span class="found">');
        reachedFoundStringBeginning = true;
      }

      resultArray.push(this.wordObjects[wordOffset]?.wordText);

      if (reachedFoundStringBeginning && !reachedFoundStringEnd) {
        foundString += this.wordObjects[wordOffset]?.wordText + ' ';
      }

      if (wordOffset === wordOffsetTermEnd) {
        resultArray.push('</span>');
        reachedFoundStringEnd = true;
      }

      resultArray.push(' ');
    }

    let searchResultOffset = 0;
    let searchResultMarker;
    // If there are topLevelMarkers with elements in the document,
    // find the marker where the word offset lies.
    Object.entries(this.#markerIndexToWordOffset).forEach(([index, offset]) => {
      if (offset === undefined) {
        return;
      }

      const maxMarkerWordOffset = Math.max(offset, searchResultOffset);

      if (maxMarkerWordOffset <= wordOffsetTermStart) {
        searchResultOffset = offset;
        searchResultMarker = this.#bookDocument.topLevelMarkers.find(
          (marker) => marker.index === +index,
        );
      }
    });

    if (!searchResultMarker) {
      // If no markers in document, assign last marker in previous document.
      let bookDocument = this.#bookDocument;
      while (bookDocument.previous) {
        if (bookDocument.topLevelMarkers.length) {
          searchResultMarker =
            bookDocument.topLevelMarkers[
              bookDocument.topLevelMarkers.length - 1
            ];

          break;
        }

        bookDocument = bookDocument.previous;
      }
    }

    return {
      html: resultArray.join('').trim(),
      foundString: foundString.trim(),
      wordOffsets,
      marker: searchResultMarker,
    };
  }

  /**
   * Calls buildSearchResult for each set of word offsets and returns an array of results.
   *
   * @param {Array<Array>} nestedWordOffsets array of arrays with search result offsets
   * @returns
   */
  buildSearchResults(nestedWordOffsets) {
    return nestedWordOffsets.map((wordOffsets) =>
      this.buildSearchResult(wordOffsets),
    );
  }

  get frameDocument() {
    return this.#frameDocument;
  }

  get bookDocument() {
    return this.#bookDocument;
  }
}

export default BookDocumentInformation;
